/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Col, Form, FormGroup, Row } from 'reactstrap';
import Parse from 'parse';
import _ from 'lodash';
import classNames from 'classnames';
import FontAwesome from 'react-fontawesome';
import FAStyles from 'styles/global/font-awesome.css';
import withStyles from 'isomorphic-style-loader/withStyles';

import { ErrorAlert } from 'helpers/alert';
import setCustomValidity from 'helpers/setCustomValidity';
import ContactLink from 'components/ContactLink';
import FloatingLabel from 'components/FloatingLabel';
import history from 'core/history';
import LoadingButton from 'components/LoadingButton';
import { loginUser } from 'actions/user';
import handleParseError from 'helpers/handleParseError';

import s from './Register.scss';

class Register extends React.Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    loginUser: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      confirmPassword: '',
      isLoading: false,
    };

    this.passwordInvalid = false;
    this.customPasswordInvalid = false;
    this.passwordRequirements =
      'Password must be 8 characters long and include both an upper and lower case letter.';
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleChange(event) {
    const { name } = event.target;
    const newState = _.extend({}, this.state);
    newState[name] = event.target.value;
    this.setState(newState);

    if (this.passwordInvalid && name === 'password') {
      this.passwordInvalid = false;
      event.target.setCustomValidity('');
    } else if (name === 'confirmPassword' || name === 'password') {
      if (this.customPasswordInvalid) {
        this.customPasswordInvalid = false;
        setCustomValidity(this.confirmPassword, '');
      }
      if (newState.password !== newState.confirmPassword) {
        this.customPasswordInvalid = true;
        setCustomValidity(this.confirmPassword, "Passwords Don't Match");
      }
    }
  }

  handleSubmit(event) {
    const keys = ['email', 'password', 'confirmPassword'];
    event.preventDefault();
    const ret = _.reduce(
      keys,
      (memo, key) => memo && this.state[key] !== '',
      true,
    );
    if (!ret) {
      ErrorAlert('Please complete the entire form.');
      return;
    }

    this.setState({ isLoading: true });

    const user = new Parse.User();
    user.set('password', this.state.password);
    user.set('email', this.state.email);
    user.set('username', this.state.email);
    user
      .signUp(null)
      .then(() => {
        this.props.loginUser(user);
        history.push('/');
      })
      .catch(
        handleParseError(error => {
          ErrorAlert(error.message);
        }),
      )
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  render() {
    return (
      <div className={classNames([s.root, 'jumbotron', 'text-center'])}>
        <div className={s.container}>
          <h1>{this.props.title}</h1>
          <Row className="justify-content-center mb-4">
            <Col sm={5} style={{ fontSize: 16 }}>
              Before you can register, you must be part of the study. If your
              institution or program has not yet registered with us, please{' '}
              <ContactLink>contact us</ContactLink> now to talk about getting
              started.
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col sm={4}>
              <Form
                className={s.textLeft}
                onSubmit={this.handleSubmit}
                method="post"
              >
                <FormGroup>
                  <Row>
                    <Col sm={12}>
                      <FloatingLabel
                        placeholder="Email"
                        id="email"
                        name="email"
                        type="email"
                        value={this.state.email}
                        inputAdditionalStyles={{
                          border: 0,
                          borderTopRightRadius: '5px',
                          borderBottomRightRadius: '5px',
                        }}
                        labelAdditionalStyles={{ left: '18%' }}
                        labelFocusedAdditionalStyles={{ color: 'white' }}
                        onChange={this.handleChange}
                        errorMessage="Please enter a valid email."
                        isRequired
                      >
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <FontAwesome name="envelope" className="fa-fw" />
                          </div>
                        </div>
                      </FloatingLabel>
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={12}>
                      <FloatingLabel
                        type="password"
                        placeholder="Password"
                        name="password"
                        id="password"
                        value={this.state.password}
                        inputAdditionalStyles={{
                          border: 0,
                          borderTopRightRadius: '5px',
                          borderBottomRightRadius: '5px',
                        }}
                        labelAdditionalStyles={{ left: '18%' }}
                        labelFocusedAdditionalStyles={{ color: 'white' }}
                        onChange={this.handleChange}
                        onInvalid={() => {
                          this.passwordInvalid = true;
                          setCustomValidity(
                            this.password,
                            this.passwordRequirements,
                          );
                        }}
                        inputRef={ref => {
                          this.password = ref;
                        }}
                        pattern={
                          /(?=^.{8,}$)((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/
                        }
                        isRequired
                      >
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <FontAwesome name="lock" className="fa-fw" />
                          </div>
                        </div>
                      </FloatingLabel>
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={12}>
                      <FloatingLabel
                        type="password"
                        placeholder="Confirm Password"
                        name="confirmPassword"
                        id="confirmPassword"
                        value={this.state.confirmPassword}
                        inputAdditionalStyles={{
                          border: 0,
                          borderTopRightRadius: '5px',
                          borderBottomRightRadius: '5px',
                        }}
                        labelAdditionalStyles={{ left: '18%' }}
                        labelFocusedAdditionalStyles={{ color: 'white' }}
                        onChange={this.handleChange}
                        inputRef={ref => {
                          this.confirmPassword = ref;
                        }}
                        isRequired
                      >
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <FontAwesome name="lock" className="fa-fw" />
                          </div>
                        </div>
                      </FloatingLabel>
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={12}>
                      <LoadingButton
                        isLoading={this.state.isLoading}
                        buttonText="Register"
                        buttonLoadingText="Register"
                        type="submit"
                        className="btn-primary-dark"
                        block
                      />
                    </Col>
                  </Row>
                </FormGroup>
              </Form>
            </Col>
          </Row>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = {
  loginUser,
};

export default connect(
  null,
  mapDispatchToProps,
)(withStyles(FAStyles, s)(Register));
